import { createRoadmap, makeMember, makePhase, uid, totalSlots, SLOTS_PER_SPRINT, MAX_SPRINT_COUNT } from './schema';
import { roadmapColour } from './palette';
import { dateToSlot, hasDates, parseDay } from './dates';

const key = (s) => String(s || '').trim().toLowerCase();

/**
 * Work out how roadmaps line up against each other.
 * When every roadmap has a start date and the same sprint length, they are aligned by date.
 * Otherwise sprint 1 of each roadmap is treated as the same sprint, as before.
 */
export function alignRoadmaps(roadmaps) {
  const list = roadmaps.filter(Boolean);
  const zero = { mode: 'sprints', offsets: list.map(() => 0), base: null, approximate: false, reason: '' };
  if (list.length < 2) return zero;
  const dated = list.filter(hasDates);
  if (!dated.length) return { ...zero, reason: 'none-dated' };
  if (dated.length !== list.length) return { ...zero, reason: 'some-dated' };
  const lengths = new Set(list.map((r) => r.settings.sprintLengthDays));
  if (lengths.size > 1) return { ...zero, reason: 'mixed-lengths' };

  const earliest = list.reduce((a, r) => (parseDay(r.settings.startDate) < parseDay(a.settings.startDate) ? r : a));
  const base = { startDate: earliest.settings.startDate, sprintLengthDays: earliest.settings.sprintLengthDays };
  let approximate = false;
  const offsets = list.map((r) => {
    const exact = dateToSlot(base, r.settings.startDate);
    const rounded = Math.round(exact);
    if (Math.abs(exact - rounded) > 0.01) approximate = true;
    return rounded;
  });
  return { mode: 'dates', offsets, base, approximate, reason: '' };
}

export function compareRoadmaps(roadmaps, colours = null, alignment = null) {
  const list = roadmaps.filter(Boolean);
  const align = alignment || alignRoadmaps(list);
  const offsetOf = (i) => align.offsets[i] || 0;
  const rawTotal = list.length ? Math.max(...list.map((r, i) => offsetOf(i) + totalSlots(r))) : 0;
  const total = Math.ceil(rawTotal / SLOTS_PER_SPRINT) * SLOTS_PER_SPRINT;

  const lanes = list.map((roadmap, i) => {
    const offset = offsetOf(i);
    const busy = new Array(total).fill(0);
    for (const p of roadmap.projects) {
      for (const b of p.blocks) {
        for (let s = b.start + offset; s < b.start + offset + b.span && s < total; s++) busy[s]++;
      }
    }
    return { roadmap, colour: colours?.[i] ?? roadmapColour(i), busy, offset };
  });

  const combined = new Array(total).fill(0).map((_, s) => lanes.filter((l) => l.busy[s] > 0).length);

  const people = new Map();
  lanes.forEach((lane, laneIndex) => {
    const names = new Map(lane.roadmap.members.map((m) => [m.id, m.name]));
    for (const p of lane.roadmap.projects) {
      for (const memberId of p.memberIds) {
        const name = names.get(memberId);
        if (!name) continue;
        const k = key(name);
        if (!people.has(k)) people.set(k, { name, slots: Array.from({ length: total }, () => new Map()) });
        const person = people.get(k);
        if (person.name === person.name.toLowerCase() && name !== name.toLowerCase()) person.name = name;
        for (const b of p.blocks) {
          const from = b.start + lane.offset;
          for (let s = from; s < from + b.span && s < total; s++) {
            const atSlot = person.slots[s];
            if (!atSlot.has(laneIndex)) atSlot.set(laneIndex, []);
            atSlot.get(laneIndex).push(p.name);
          }
        }
      }
    }
  });

  const clashes = [...people.values()]
    .map((person) => {
      const perSlot = person.slots.map((m) =>
        m.size >= 2 ? [...m.entries()].map(([lane, projects]) => ({ lane, projects })) : []
      );
      return { name: person.name, perSlot, clashSlots: perSlot.filter((x) => x.length).length };
    })
    .filter((p) => p.clashSlots > 0)
    .sort((a, b) => b.clashSlots - a.clashSlots || a.name.localeCompare(b.name));

  return {
    total,
    sprintCount: total / SLOTS_PER_SPRINT,
    lanes,
    combined,
    gapSlots: combined.filter((n) => n === 0).length,
    overlapSlots: combined.filter((n) => n >= 2).length,
    clashes,
    alignment: align
  };
}

export function mergeRoadmaps(roadmaps, { name, prefixNames = false } = {}) {
  const align = alignRoadmaps(roadmaps);
  const members = [];
  const memberByKey = new Map();
  const phases = [];
  const phaseByKey = new Map();

  for (const rm of roadmaps) {
    for (const m of rm.members) {
      if (!memberByKey.has(key(m.name))) {
        const nm = makeMember(m.name, { capacity: m.capacity ?? 100, away: [] });
        members.push(nm);
        memberByKey.set(key(m.name), nm.id);
      } else {
        const known = members.find((x) => x.id === memberByKey.get(key(m.name)));
        if (known.name === known.name.toLowerCase() && m.name !== m.name.toLowerCase()) known.name = m.name;
      }
    }
    for (const p of rm.phases) {
      if (!phaseByKey.has(key(p.name))) {
        const np = makePhase(p.name, p.color);
        phases.push(np);
        phaseByKey.set(key(p.name), np.id);
      }
    }
  }

  const span = Math.max(...roadmaps.map((r, i) => (align.offsets[i] || 0) + totalSlots(r)));
  const merged = createRoadmap({
    name: name || roadmaps.map((r) => r.name).join(' + '),
    members,
    phases,
    settings: {
      sprintCount: Math.min(MAX_SPRINT_COUNT, Math.ceil(span / SLOTS_PER_SPRINT)),
      halfSprints: roadmaps.some((r) => r.settings.halfSprints) || align.offsets.some((o) => o % 2 !== 0),
      startDate: align.base?.startDate ?? null,
      sprintLengthDays: align.base?.sprintLengthDays ?? roadmaps[0]?.settings.sprintLengthDays
    }
  });
  const limit = merged.settings.sprintCount * SLOTS_PER_SPRINT;

  // Away sprints carry over when the person exists in a roadmap on the same timeline.
  roadmaps.forEach((rm, i) => {
    const offsetSprints = (align.offsets[i] || 0) / SLOTS_PER_SPRINT;
    if (!Number.isInteger(offsetSprints)) return;
    for (const m of rm.members) {
      const target = members.find((x) => x.id === memberByKey.get(key(m.name)));
      for (const s of m.away || []) {
        const n = s + offsetSprints;
        if (n >= 1 && n <= merged.settings.sprintCount && !target.away.includes(n)) target.away.push(n);
      }
      target.away.sort((a, b) => a - b);
    }
  });

  roadmaps.forEach((rm, i) => {
    const offset = align.offsets[i] || 0;
    const memberName = new Map(rm.members.map((m) => [m.id, m.name]));
    const phaseName = new Map(rm.phases.map((p) => [p.id, p.name]));
    const projectIds = new Map(rm.projects.map((p) => [p.id, uid()]));
    const mapMember = (id) => memberByKey.get(key(memberName.get(id)));
    for (const p of rm.projects) {
      merged.projects.push({
        id: projectIds.get(p.id),
        name: prefixNames ? `${rm.name}: ${p.name}` : p.name,
        description: p.description,
        memberIds: p.memberIds.map(mapMember).filter(Boolean),
        allocation: Object.fromEntries(Object.entries(p.allocation || {}).map(([id, v]) => [mapMember(id), v]).filter(([id]) => id)),
        group: p.group || '',
        status: p.status || '',
        links: (p.links || []).map((l) => ({ ...l, id: uid() })),
        dependsOn: (p.dependsOn || []).map((id) => projectIds.get(id)).filter(Boolean),
        blocks: p.blocks
          .map((b) => ({
            id: uid(),
            phaseId: b.phaseId ? phaseByKey.get(key(phaseName.get(b.phaseId))) ?? null : null,
            start: b.start + offset,
            span: b.span
          }))
          .filter((b) => b.start < limit)
          .map((b) => ({ ...b, span: Math.min(b.span, limit - b.start) }))
      });
    }
    for (const m of rm.milestones || []) {
      const slot = m.slot + offset;
      if (slot <= limit) merged.milestones.push({ id: uid(), name: prefixNames ? `${rm.name}: ${m.name}` : m.name, slot, date: m.date || null });
    }
  });
  merged.milestones.sort((a, b) => a.slot - b.slot);

  return merged;
}
